import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { ActionSelectionScreen } from './ActionSelectionScreen';
import { SuccessState } from './shared/SuccessState';
import {
    getRequestTypeKey,
    programRequestTypeRegistry,
} from './programRequestTypeRegistry';
import { programRequestsApi } from './api/programRequestsApi';
import { createFlowRegistry } from './createFlowRegistry';
import { editFlowRegistry } from './editFlowRegistry';

export const ProgramRequestModalShell = ({
    isOpen,
    toggle,
    institutionOptions = [],
    programsByInstitution = {},
    requestTypeRegistry = programRequestTypeRegistry,
    mode = 'create',
    requestDetails,
    onSuccess,
}) => {
    const [selectedAction, setSelectedAction] = useState(null);
    const [succeeded, setSucceeded] = useState(false);
    const [maximumAdditionalCommentsLength, setMaximumAdditionalCommentsLength] =
        useState(null);

    useEffect(() => {
        if (!isOpen || maximumAdditionalCommentsLength !== null) return;

        programRequestsApi
            .getConfiguration(() => null)
            .then(config => {
                const maximum = Number(config?.maximumAdditionalCommentsLength);
                if (Number.isFinite(maximum) && maximum > 0) {
                    setMaximumAdditionalCommentsLength(maximum);
                }
            });
    }, [isOpen, maximumAdditionalCommentsLength]);

    const resetAndToggle = () => {
        setSelectedAction(null);
        setSucceeded(false);
        toggle();
    };

    const handleSelect = (action) => {
        setSelectedAction(action);
        setSucceeded(false);
    };

    const handleBack = () => {
        setSelectedAction(null);
        setSucceeded(false);
    };

    const handleSuccess = () => {
        if (onSuccess) onSuccess();

        if (mode === 'edit') {
            resetAndToggle();
            return;
        }
        setSucceeded(true);
    };

    const fixedRequestTypeKey = mode === 'edit'
        ? getRequestTypeKey(requestDetails?.typeId, requestTypeRegistry)
        : null;
    const activeRequestTypeKey = fixedRequestTypeKey ?? selectedAction;

    const selectedRequestType = activeRequestTypeKey
        ? requestTypeRegistry[activeRequestTypeKey]
        : null;

    const modalTitlePrefix = mode === 'edit' ? 'Edit Staff Action Request' : 'New Routine Staff Action Request';
    const modalTitle = selectedRequestType
        ? `${modalTitlePrefix} — ${selectedRequestType.label}`
        : modalTitlePrefix;

    const FormComponent = selectedRequestType?.Form ?? null;
    const operationRegistry = mode === 'edit'
        ? editFlowRegistry
        : createFlowRegistry;
    const operation = activeRequestTypeKey
        ? operationRegistry[activeRequestTypeKey]
        : null;

    return (
        <Modal isOpen={isOpen} toggle={resetAndToggle} size="lg">
            <ModalHeader toggle={resetAndToggle}>{modalTitle}</ModalHeader>
            <ModalBody>
                {mode === 'create' && !selectedAction && (
                    <ActionSelectionScreen
                        onSelect={handleSelect}
                        requestTypes={requestTypeRegistry}
                    />
                )}
                {mode === 'create' && selectedAction && succeeded && (
                    <SuccessState
                        onBackToSelection={handleBack}
                        onDone={resetAndToggle}
                    />
                )}
                {isOpen && activeRequestTypeKey && !succeeded && FormComponent && operation && (
                    <FormComponent
                        key={`${mode}-${activeRequestTypeKey}-${requestDetails?.requestID ?? 'new'}`}
                        onBack={mode === 'edit' ? resetAndToggle : handleBack}
                        onSuccess={handleSuccess}
                        institutionOptions={institutionOptions}
                        programsByInstitution={programsByInstitution}
                        maximumAdditionalCommentsLength={maximumAdditionalCommentsLength}
                        mode={mode}
                        requestDetails={requestDetails}
                        initialModel={operation.initialize(requestDetails)}
                        submitFlow={operation.submit}
                    />
                )}
                {mode === 'edit' && !activeRequestTypeKey && (
                    <div className="alert alert-danger" role="alert">
                        This program request type is not supported by the shared editor.
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};
